export const NOTIFICATION_PAGE_SIZE = 20;
export const NOTIFICATION_PREVIEW_SIZE = 10;

export const NOTIFICATION_TYPES = [
  "comment_on_post",
  "comment_reply",
  "reaction_on_post",
  "mention",
  "community_invite",
  "community_join_request",
  "community_join_approved",
  "event_registration_confirmed",
  "event_updated",
  "event_cancelled",
  "event_reminder",
  "chat_direct_message",
  "chat_group_message",
  "service_request_created",
  "service_request_status_changed",
  "service_offer_received",
  "service_document_received",
  "practice_invite",
  "practice_join_request",
  "practice_join_approved",
  "practice_join_declined",
  "practice_invite_accepted",
  "practice_invite_declined",
  "system_announcement",
  "system_message",
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export type NotificationCategory =
  | "community"
  | "chat"
  | "service"
  | "system";

export type EmailDigestFrequency = "immediate" | "daily" | "weekly" | "off";

export type NotificationListFilter = "all" | "unread" | NotificationCategory;

export type AppNotification = {
  id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  actorId: string | null;
  entityType: string | null;
  entityId: string | null;
  targetPath: string;
  isRead: boolean;
  createdAt: string;
};

export type NotificationPreferences = {
  communityInApp: boolean;
  chatInApp: boolean;
  serviceInApp: boolean;
  systemInApp: boolean;
  communityEmail: boolean;
  chatEmail: boolean;
  serviceEmail: boolean;
  systemEmail: boolean;
  emailDigest: EmailDigestFrequency;
  communityPush: boolean;
  chatPush: boolean;
  servicePush: boolean;
  systemPush: boolean;
};

export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> =
  {
    community: "Közösség",
    chat: "Chat",
    service: "Szolgáltatások",
    system: "Rendszer",
  };

const COMMUNITY_TYPES: NotificationType[] = [
  "comment_on_post",
  "comment_reply",
  "reaction_on_post",
  "mention",
  "community_invite",
  "community_join_request",
  "community_join_approved",
  "event_registration_confirmed",
  "event_updated",
  "event_cancelled",
  "event_reminder",
];

const CHAT_TYPES: NotificationType[] = [
  "chat_direct_message",
  "chat_group_message",
];

const SERVICE_TYPES: NotificationType[] = [
  "service_request_created",
  "service_request_status_changed",
  "service_offer_received",
  "service_document_received",
];

const SYSTEM_TYPES: NotificationType[] = [
  "practice_invite",
  "practice_join_request",
  "practice_join_approved",
  "practice_join_declined",
  "practice_invite_accepted",
  "practice_invite_declined",
  "system_announcement",
  "system_message",
];

export function isNotificationType(value: string): value is NotificationType {
  return NOTIFICATION_TYPES.includes(value as NotificationType);
}

export function categoryForNotificationType(
  type: NotificationType,
): NotificationCategory {
  if (COMMUNITY_TYPES.includes(type)) {
    return "community";
  }
  if (CHAT_TYPES.includes(type)) {
    return "chat";
  }
  if (SERVICE_TYPES.includes(type)) {
    return "service";
  }
  return "system";
}

export function typesForCategory(
  category: NotificationCategory,
): NotificationType[] {
  switch (category) {
    case "community":
      return COMMUNITY_TYPES;
    case "chat":
      return CHAT_TYPES;
    case "service":
      return SERVICE_TYPES;
    case "system":
      return SYSTEM_TYPES;
  }
}

export function targetPathForNotification(input: {
  targetPath: string | null;
  type: NotificationType;
  entityType: string | null;
  entityId: string | null;
}) {
  if (input.targetPath) {
    return input.targetPath;
  }

  if (
    (input.type === "chat_direct_message" ||
      input.type === "chat_group_message") &&
    input.entityId
  ) {
    return `/chat/${input.entityId}`;
  }

  if (
    (input.type === "community_invite" ||
      input.type === "community_join_request" ||
      input.type === "community_join_approved") &&
    input.entityType === "community"
  ) {
    return "/communities";
  }

  if (
    input.type === "service_request_created" ||
    input.type === "service_request_status_changed"
  ) {
    return "/services/requests";
  }

  if (input.type === "service_offer_received") {
    return input.entityId ? `/services/offers/${input.entityId}` : "/services/offers";
  }

  if (input.type === "service_document_received") {
    if (input.entityType === "customer" && input.entityId) {
      return `/services/active/${input.entityId}`;
    }
    return input.entityId ? `/services/offers/${input.entityId}` : "/services/documents";
  }

  if (
    input.type === "comment_on_post" ||
    input.type === "comment_reply" ||
    input.type === "reaction_on_post" ||
    input.type === "mention"
  ) {
    return "/feed";
  }

  if (
    input.type === "event_registration_confirmed" ||
    input.type === "event_updated" ||
    input.type === "event_cancelled" ||
    input.type === "event_reminder"
  ) {
    return "/events";
  }

  if (
    input.type === "practice_invite" ||
    input.type === "practice_join_request" ||
    input.type === "practice_join_approved" ||
    input.type === "practice_join_declined" ||
    input.type === "practice_invite_accepted" ||
    input.type === "practice_invite_declined"
  ) {
    return input.entityId ? `/practices/${input.entityId}` : "/practices";
  }

  return "/notifications";
}
